import {
  ChangeDetectionStrategy,
  Component,
  EventEmitter,
  Input,
  Output,
} from '@angular/core';
import { IDynamicItem } from '@epicuro-next/models/platform/dynamic-elements/dynamic-elements';
import { RecommendationsPanels } from '@epicuro-next/models/recommendation-panels';
import { selectRecommendationSlotsFromMerchandiser } from '@epicuro-next/platform/configuration';
import { selectActiveAccount } from '@epicuro-next/state/accounts-state';
import { selectUrl } from '@epicuro-next/state/router-store';
import { select, Store } from '@ngrx/store';
import { Account } from 'graphql-api/gql-api-interfaces';
import {
  BehaviorSubject,
  combineLatest,
  debounceTime,
  distinctUntilChanged,
  distinctUntilKeyChanged,
  iif,
  map,
  Observable,
  of,
  shareReplay,
  switchMap,
  tap,
} from 'rxjs';

import { RecommendationPanelsProviderService } from '../../services/recommendation-panels-provider.service';

type ItemsArguments = [
  Record<RecommendationsPanels, IDynamicItem[]>,
  keyof typeof RecommendationsPanels,
  string,
  string[],
  Account,
];

@Component({
  selector: 'epc-recommendation-panel',
  templateUrl: './recommendation-panel.component.html',
  styleUrls: ['./recommendation-panel.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  standalone: false,
})
export class RecommendationPanelComponent {
  @Input() set location(value: keyof typeof RecommendationsPanels) {
    this.location$.next(value);
  }

  @Output() public hasItems = new EventEmitter<boolean>();

  private panels$: Observable<{
    [key in keyof typeof RecommendationsPanels]: IDynamicItem[];
  }> = this.recommendationPanelsProviderService.recommendationsPanelsItems$;

  private location$ = new BehaviorSubject<
    keyof typeof RecommendationsPanels | null
  >(null);

  public readonly items$ = combineLatest([
    this.panels$,
    this.location$,
    this.store.pipe(select(selectUrl)),
    this.store.pipe(select(selectRecommendationSlotsFromMerchandiser)),
    this.store.pipe(select(selectActiveAccount)),
  ]).pipe(
    debounceTime(300),
    distinctUntilChanged(
      (prev: ItemsArguments, curr: ItemsArguments) => prev[2] === curr[2],
    ), // until URL is changed
    switchMap(([config, location, url, slots, _]: ItemsArguments) => {
      return iif(
        () => slots?.includes(location as string),
        this.recommendationPanelsProviderService
          .merchandiseRecommendationsPanel(location as string)
          .pipe(
            map((response) => {
              if (response && location) {
                const result = response
                  .filter((panel) => {
                    return (
                      panel.pathPredicate === undefined ||
                      url.includes(panel.pathPredicate as string)
                    );
                  })
                  .map((panel) => panel.panel)
                  .flat();
                if (result !== undefined) {
                  config[location] = result;
                }
              }
              return { config, location };
            }),
          ),
        of({ config, location }),
      );
    }),
    map(({ config, location }) => {
      const items: IDynamicItem[] = [];
      if (location && config) {
        items.push(...config[location]);
      }

      return { json: JSON.stringify(items), items };
    }),
    distinctUntilKeyChanged('json'),
    map(({ items }) => items),
    shareReplay({ bufferSize: 1, refCount: true }),
  );

  public readonly shouldShow$ = this.items$.pipe(
    map((items) => items && items.length > 0),
    tap((shouldShow) => {
      this.hasItems.next(shouldShow);
    }),
  );

  constructor(
    private readonly recommendationPanelsProviderService: RecommendationPanelsProviderService,
    private store: Store,
  ) {}
}
